import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BASE_URL || 'https://www.my.epik.io';

function toClickhouseFormat(date) {
    if (!date) return null;
  
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Los_Angeles',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  
    const parts = formatter.formatToParts(date);
    const dateParts = {};
    for (const part of parts) {
      dateParts[part.type] = part.value;
    }
  
    return `${dateParts.year}-${dateParts.month}-${dateParts.day} ${dateParts.hour}:${dateParts.minute}:${dateParts.second}`;
}

export const historicalDataApi = {
  getHeaders() {
    const token = localStorage.getItem('jwtToken');
    if (!token) {
      throw new Error('Authentication token not found');
    }
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  },

  /**
   * Fetch historical data for a device with pagination and time range filtering
   * @param {string} deviceId - The ID of the device to fetch data for
   * @param {string} timeRange - Time range (24h, 7d, 30d, all)
   * @param {string} lastTimestamp - Timestamp for pagination (exclusive)
   * @returns {Promise<Array>} - Historical metrics data
   */
  async fetchHistoricalData(deviceId, timeRange = '24h', lastTimestamp = null, compareColumns) {
    try {
      // Convert time range to timestamp
      const now = new Date();
      let startTime;
      
      switch (timeRange) {
        case '7d':
          startTime = toClickhouseFormat(new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000));
          break;
        case '30d':
          startTime = toClickhouseFormat(new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000));
          break;
        case 'all':
          startTime = toClickhouseFormat(new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000));;
          break;
        case '24h':
        default:
          startTime = toClickhouseFormat(new Date(now.getTime() - 24 * 60 * 60 * 1000));
          break;
      }

      const response = await axios.post(`${API_BASE_URL}/apps/api/analytics/device/metrics`, {
        columns: [ 'timestamp', 'serialNumber', 'model', 'displayName', ...compareColumns],
        deviceId,
        startTime,
        endTime: '',
        whereConditions: [],
        compareColumns
      }, {
        headers: this.getHeaders(),
      });
      
      return response.data;
    } catch (error) {
      console.error('Error fetching historical data:', error);
      throw error;
    }
  },
  
  /**
   * Fetch all historical data for a time range with automatic pagination
   * @param {string} deviceId - The ID of the device to fetch data for
   * @param {string} timeRange - Time range (24h, 7d, 30d, all)
   * @param {number} pageSize - Number of records per page
   * @returns {Promise<Array>} - Complete historical metrics data
   */
  async fetchAllHistoricalData(deviceId, timeRange = '24h', category ) {
    let allData = [];
    let lastTimestamp = null;

    const compareColumns = category?.map((item) => item?.key);
    try {
        const data = await this.fetchHistoricalData(deviceId, timeRange, lastTimestamp, compareColumns);
        
        if (data && data.length > 0) {
          allData = [...allData, ...data];
        }
      
      return allData;
    } catch (error) {
      console.error('Error fetching all historical data:', error);
      throw error;
    }
  },

  async fetchHistoricalDataWithPagination(params) {
    try {
      const response = await axios.post(`${API_BASE_URL}/apps/api/analytics/device/all-data`,
        {
          deviceId: params.deviceId,
          pageSize: params.pageSize,
          sortField: params.sortField,
          sortDirection: params.sortDirection,
          lastTimeStamp: params?.lastTimeStamp,
          navigateType: params?.navigateType,
          filters: params?.filters,
          page: params?.page,
        },
        {
          headers: this.getHeaders(),
        }
      );

      return response?.data;
    } catch (error) {
      console.error('Error fetching historical data:', error);
      throw error;
    }
  },

  async fetchHistoricalDataLastValue(params) {
    try {
      const response = await axios.post(`${API_BASE_URL}/apps/api/analytics/device/last-value`,
        {
          deviceId: params.deviceId,
          columns: params.columns
        },
        {
          headers: this.getHeaders(),
        }
      );

      return response?.data;
    } catch (error) {
      console.error('Error fetching historical data:', error);
      throw error;
    }
  }
};
